/* Shared "Atelier" chrome + helpers for the manage surfaces (Dashboard,
   Quizzes, Builder, Bank, Results, Account, Team, Auth, modals). Refined,
   editorial language — Sprig serif, hairline borders, muted palette.
   Styling lives in bakeoff.css (.atl scope). Wired to the real router/auth. */
import { useState, type CSSProperties, type ReactNode } from "react";
import { Logo } from "./motifs";
import { Ico } from "./shell";
import { navigate } from "../router";
import { useAuth } from "../auth";

/* The GAIL's × BakeOff lockup is just the shared Logo; alias for parity with
   the design files. */
export const AtlLogo = Logo;

function initialsOf(name: string): string {
  const w = name.trim().split(/\s+/).filter(Boolean);
  if (w.length >= 2) return (w[0][0] + w[1][0]).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase() || "??";
}

/** Primary nav, shown to every signed-in user. [icon, label, path]. */
const NAV: [string, string, string][] = [
  ["home", "Dashboard", "/"],
  ["build", "Quizzes", "/quizzes"],
  ["bank", "Question Bank", "/bank"],
  ["chart", "Results", "/results"],
  ["user", "Account", "/account"],
];

function NavRow({ ic, label, to, active, onGo }: { ic: string; label: string; to: string; active: string; onGo: (to: string) => void }) {
  const on = active === label;
  return (
    <div
      onClick={() => onGo(to)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 13,
        padding: "12px 14px",
        borderRadius: 10,
        fontWeight: on ? 600 : 500,
        fontSize: 14.5,
        cursor: "pointer",
        color: on ? "var(--atl-accent)" : "var(--atl-soft)",
        background: on ? "#fbf2f3" : "transparent",
        boxShadow: on ? "inset 2px 0 0 var(--atl-accent)" : "none",
      }}
    >
      <Ico name={ic} size={18} /> {label}
    </div>
  );
}

/** A compact icon button (admin row); the label shows as a hover tooltip. */
function AdminIcon({ ic, label, to, active, onGo }: { ic: string; label: string; to: string; active: string; onGo: (to: string) => void }) {
  const on = active === label;
  return (
    <button
      className={`atl-rail-item${on ? " atl-rail-item--on" : ""}`}
      data-tip={label}
      aria-label={label}
      onClick={() => onGo(to)}
    >
      <Ico name={ic} size={18} />
    </button>
  );
}

/* The nav body shared by the desktop rail and the mobile drawer. `onGo` lets the
   drawer close itself on navigation. */
function SidebarBody({ active, onGo }: { active: string; onGo: (to: string) => void }) {
  const { user, logout } = useAuth();
  const name = user?.displayName ?? "Baker";
  const role = user?.role === "super_admin" ? "Super admin" : user?.role === "admin" ? "Admin" : "Host";
  const isAdminish = user?.role === "admin" || user?.role === "super_admin";
  const isSuper = user?.role === "super_admin";

  // Admin/super functions, shown as a compact icon row at the bottom (admins get
  // Team + Recycle bin; super admins also get the three operational areas).
  const manage: [string, string, string][] = [];
  if (isAdminish) {
    manage.push(["team", "Team", "/team"], ["trash", "Recycle bin", "/recycle-bin"]);
  }
  if (isSuper) {
    manage.push(["clock", "Logs", "/logs"], ["layers", "All Quizzes", "/all-quizzes"], ["play", "Live", "/live"]);
  }

  return (
    <>
      <div style={{ padding: "0 8px", cursor: "pointer" }} onClick={() => onGo("/")}>
        <AtlLogo />
      </div>
      <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {NAV.map(([ic, label, to]) => (
          <NavRow key={label} ic={ic} label={label} to={to} active={active} onGo={onGo} />
        ))}
      </nav>
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
        {manage.length > 0 && (
          <div className="atl-adminzone">
            <div className="atl-eyebrow" style={{ padding: "0 4px 8px", margin: 0 }}>
              Admin zone
            </div>
            <div className="atl-adminrow">
              {manage.map(([ic, label, to]) => (
                <AdminIcon key={label} ic={ic} label={label} to={to} active={active} onGo={onGo} />
              ))}
            </div>
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 11,
            padding: "10px 12px",
            borderRadius: 13,
            border: "1px solid var(--atl-line)",
            background: "var(--atl-surface-2)",
          }}
        >
          <div
            onClick={() => onGo("/account")}
            style={{ display: "flex", alignItems: "center", gap: 11, flex: 1, minWidth: 0, cursor: "pointer" }}
          >
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: 11,
                background: "var(--atl-gold)",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontWeight: 700,
                fontSize: 14,
                flex: "0 0 auto",
              }}
            >
              {initialsOf(name)}
            </span>
            <div style={{ lineHeight: 1.3, flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5, overflowWrap: "anywhere" }}>{name}</div>
              <div style={{ fontSize: 12, color: "var(--atl-faint)", whiteSpace: "nowrap" }}>{role}</div>
            </div>
          </div>
          <button
            className="atl-iconbtn"
            title="Sign out"
            style={{ width: 32, height: 32, flex: "0 0 auto" }}
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
          >
            <Ico name="export" size={15} />
          </button>
        </div>
      </div>
    </>
  );
}

/* Desktop rail. Hidden < 860px via the `.atl-sidebar` media rule; the mobile
   drawer takes over there. */
export function AtlSidebar({ active }: { active: string }) {
  return (
    <aside className="atl-sidebar">
      <SidebarBody active={active} onGo={(to) => navigate(to)} />
    </aside>
  );
}

export function AtlTopBar({ title, sub, children, onMenu }: { title: string; sub?: string; children?: ReactNode; onMenu?: () => void }) {
  return (
    <header className="atl-topbar">
      {onMenu && (
        <button className="atl-menubtn" aria-label="Open menu" onClick={onMenu}>
          <Ico name="menu" size={20} />
        </button>
      )}
      <div className="atl-topbar__titles" style={{ minWidth: 0 }}>
        <h1 className="atl-serif atl-topbar__title" style={{ fontSize: 25 }}>
          {title}
        </h1>
        {sub && (
          <div className="atl-eyebrow" style={{ marginTop: 4 }}>
            {sub}
          </div>
        )}
      </div>
      <div className="atl-topbar__spacer" style={{ flex: 1 }} />
      {children}
    </header>
  );
}

export function AtlSearch({
  placeholder = "Search…",
  w = 250,
  value,
  onChange,
}: {
  placeholder?: string;
  w?: number;
  value?: string;
  onChange?: (v: string) => void;
}) {
  return (
    <div
      className="atl-search"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        width: w,
        padding: "10px 15px",
        background: "var(--atl-surface-2)",
        border: "1px solid var(--atl-line)",
        borderRadius: 999,
        color: "var(--atl-faint)",
      }}
    >
      <Ico name="search" size={16} />
      {onChange ? (
        <input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            background: "transparent",
            fontFamily: "inherit",
            fontSize: 13.5,
            fontWeight: 500,
            color: "var(--atl-ink)",
          }}
        />
      ) : (
        <span style={{ fontSize: 13.5, fontWeight: 500 }}>{placeholder}</span>
      )}
    </div>
  );
}

/* Layout helper: the standard Atelier screen frame (sidebar + content column).
   Children render inside the scrolling content column. */
export function AtlShell({
  active,
  title,
  sub,
  actions,
  children,
}: {
  active: string;
  title: string;
  sub?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const [drawer, setDrawer] = useState(false);
  const close = () => setDrawer(false);
  return (
    <div className="atl atl-shell" style={{ display: "flex" }}>
      <AtlSidebar active={active} />
      <div className="atl-main" style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <AtlTopBar title={title} sub={sub} onMenu={() => setDrawer(true)}>
          {actions}
        </AtlTopBar>
        {children}
      </div>

      {/* Mobile slide-in nav drawer */}
      <div className={`atl-drawer-scrim${drawer ? " is-open" : ""}`} onClick={close} />
      <aside className={`atl-drawer${drawer ? " is-open" : ""}`} aria-hidden={!drawer}>
        <button className="atl-drawer__close atl-iconbtn" aria-label="Close menu" onClick={close}>
          <Ico name="close" size={16} />
        </button>
        <SidebarBody
          active={active}
          onGo={(to) => {
            close();
            navigate(to);
          }}
        />
      </aside>
    </div>
  );
}

/* The GAIL's "G" roundel (from gails.com favicon) — the standalone brand mark
   used when a quiz has no cover image. */
export function GailsG({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
      <path
        d="m19.27,9.95c.05,2.54-.22,4.63-1.94,6.62-1.72,1.99-4.33,3.13-6.94,3.13C5.11,19.7.73,15.33.73,10.05S5.11.3,10.51.3c3.28,0,5.95,1.47,7.94,4.06l-1.52,1.42c-1.57-2.21-3.68-3.53-6.45-3.53-4.28,0-7.66,3.61-7.66,7.84,0,1.99.8,3.88,2.19,5.3,1.37,1.37,3.36,2.39,5.33,2.39,3.26,0,6.69-2.39,6.69-5.87h-6.74v-1.94h8.98Z"
        style={{ fill: "var(--atl-accent)", strokeWidth: 0 }}
      />
    </svg>
  );
}

/* Small quiz mark — the quiz's chosen emoji, or (none) the GAIL's G, on a
   colour-spined tile. */
export function QuizMark({ c, size = 46, emoji }: { c: string; size?: number; emoji?: string }) {
  return (
    <span
      className="atl-tile"
      style={{ width: size, height: size, position: "relative", overflow: "hidden", display: "grid", placeItems: "center" }}
    >
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: c }} />
      {emoji ? (
        <span style={{ fontSize: Math.round(size * 0.5), lineHeight: 1 }}>{emoji}</span>
      ) : (
        <GailsG size={Math.round(size * 0.5)} />
      )}
    </span>
  );
}

export function StatusChip({ status }: { status: string }) {
  const pub = status === "published";
  return (
    <span
      className="atl-chip"
      style={{
        fontSize: 11.5,
        padding: "4px 11px",
        textTransform: "capitalize",
        borderColor: pub ? "#cfe0c2" : "#e7d6ac",
        color: pub ? "#5d7344" : "#8a6a23",
        background: pub ? "#f1f5ea" : "#fbf5e6",
      }}
    >
      <span className="atl-dot" style={{ background: pub ? "var(--atl-c)" : "var(--atl-gold)" }} /> {status}
    </span>
  );
}

/* Colour palette by answer index → the Atelier answer hue. */
export const ATL_ANS = ["var(--atl-a)", "var(--atl-b)", "var(--atl-c)", "var(--atl-d)"];

/* ---- Modal building blocks (Atelier) ---- */
export function AtlModalOverlay({ children, onClose }: { children: ReactNode; onClose?: () => void }) {
  return (
    <div
      className="atl"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "grid",
        placeItems: "center",
        padding: 26,
        background: "rgba(43,30,20,.42)",
        minHeight: "100dvh",
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "100%", display: "grid", placeItems: "center" }}>
        {children}
      </div>
    </div>
  );
}

export function AtlModalShell({
  icon,
  iconBg,
  iconFg,
  title,
  sub,
  onClose,
  children,
  max = 520,
}: {
  icon: ReactNode;
  iconBg: string;
  iconFg: string;
  title: string;
  sub?: string;
  onClose?: () => void;
  children?: ReactNode;
  max?: number;
}) {
  return (
    <div
      className="atl-card"
      style={{ maxWidth: max, width: "100%", margin: "0 auto", padding: 26, boxShadow: "0 30px 80px rgba(40,25,15,.3)" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
        <span
          style={{ width: 44, height: 44, borderRadius: 13, background: iconBg, color: iconFg, display: "grid", placeItems: "center", flex: "0 0 auto" }}
        >
          {icon}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 className="atl-serif" style={{ fontSize: 21 }}>
            {title}
          </h3>
          {sub && (
            <p style={{ fontSize: 13, color: "var(--atl-soft)", fontWeight: 500, marginTop: 2 }}>{sub}</p>
          )}
        </div>
        {onClose && (
          <button className="atl-iconbtn" style={{ border: "none" }} onClick={onClose}>
            <XIcon />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

export function XIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

/* Sparkle glyph used by the AI / generate surfaces. */
export function SparkleIcon({ size = 22, style }: { size?: number; style?: CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M12 3c.7 4 1.6 4.9 5.6 5.6C13.6 9.3 12.7 10.2 12 14c-.7-3.8-1.6-4.7-5.6-5.4C10.4 7.9 11.3 7 12 3Z" />
      <path d="M18 14l.5 2 2 .5-2 .5L18 19l-.5-2-2-.5 2-.5Z" />
    </svg>
  );
}
